/**
 * 시스템 설정 — 「거래 자료 메일」 탭(EA-73)·판 보기(EA-74)·새 판(EA-75).
 *
 * 요구: 거래 흐름 04 8단계 — 사장님 원문 8 「거래소 관리자는 관련 거래 자료들을 확인하고 사용자에게 관련
 * 자료들을 메일로 보내준다」, 2026-10-01 「거래소 관리자 약관처럼 거래소관리자에서 관리 할 수 있도록
 * 만들어라」, 「시작해」. 지갑거래서명약관(EA-65~EA-67)과 같은 방식이다 — 고치면 새 판, 옛 판은 지우지 않는다.
 *
 * 2026-10-02 영어로만(사장님 「기본언어는 영어이고 한국어는 제거해야 될 수도 있다」, 「일단 영어로만 설정해줘 …」 —
 * 거래소백엔드 1ab5d45): 한 판 = 영어 한 벌(제목·본문). 2026-10-01 의 한국어·영어 두 벌(질문 23·24)은 없어졌다 — 옛 판의
 * 한국어 칸(`subjectKo`·`bodyKo`)은 서버에 기록으로만 남고 메일에 쓰지 않아 이 화면은 보이지 않는다. 한글이 있으면
 * [Create]를 잠근다(서버도 400 `korean_not_allowed`).
 *
 * 계약: XM-110(목록)·XM-111(시행 중)·XM-112(한 판) — 키 `terms.view`, XM-113(새 판 `{subject, body, effectiveAt?}`) —
 * 키 `terms.edit`(거래소백엔드 `TradeMaterialMailTemplateController`, 912775e·5df107e·1ab5d45).
 *
 * 사고 상세의 [자료 메일 보내기](EA-76)가 시행 중인 판으로 창을 채우고(XM-117), 관리자가 고친 글 뒤에 서버가 영어 거래
 * 자료를 붙인다(본문 안 자리표시는 없다 — 글 그대로 간다). 처음에는 판이 없었다(V522) — 판이 없으면 자료 메일을 보낼 수
 * 없다(409 `mail_template_missing`).
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createTradeMaterialMailTemplate, getEffectiveTradeMaterialMailTemplate, getTradeMaterialMailTemplate,
  listTradeMaterialMailTemplates,
} from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import { AdminRef, Badge, Button, Field, Notice, Row, TD, TextArea, TH, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite, ViewDialog } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { effectiveMillis, orNullOn404, Plain } from '@/components/Versioned'
import { DateTimeField } from '@/components/DateFields'
import { EnglishOnlyNote, hasKorean } from '@/components/EnglishOnly'
import type { TradeMaterialMailTemplate } from '@/api/types'

/** 탭·창 이름 — 거래 흐름 04 8단계(합의한 이름)를 영어로. */
export const MATERIAL_MAIL = 'Trade material mail'

/** 서버가 본문 뒤에 붙이는 것(계약 XM-114 '만든 대로' — 2026-10-02 영어). 탭 안내·새 판 창이 같은 글을 쓴다. */
const APPENDED = "When sending, the server appends the trade materials after the body (trade number, order number, recipient, amount, fee, stage history with times, deposit transaction, closing transaction, and for each of the receiving member's own photos the upload time and the reading result), and attaches that member's proof photos as files."

/** 새 판 창의 칸 아래 안내·막힘 글(01 14-4 와 같은 꼴). */
const HINT = 'Material mail is sent to members in English. Write the subject and body in English.'
const BLOCKED = 'The subject or body has Korean letters. [Create] stays off until they are written in English only.'

/** XM-111 — 지금 보내면 쓰는 판, 없으면(404) null. */
const effectiveMailTemplateOrNull = () => orNullOn404(getEffectiveTradeMaterialMailTemplate)

/** EA-73 — 거래 자료 메일 탭(XM-110·XM-111). */
export function MaterialMailTemplateTab() {
  const { has } = useSession()
  const list = useQuery({ queryKey: ['trade-material-mail-templates'], queryFn: listTradeMaterialMailTemplates })
  const effective = useQuery({
    queryKey: ['trade-material-mail-templates', 'effective'], queryFn: effectiveMailTemplateOrNull,
  })
  const [viewing, setViewing] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  // 판 새것부터(서버도 새 판 먼저 준다).
  const rows = [...(list.data ?? [])].sort((a, b) => b.version - a.version)
  const current = effective.data?.version ?? null

  return (
    <>
      <p className="mb-3 text-xs text-slate-500">
        The mail subject and body that [Send material mail] in the incident details uses. The send dialog is filled
        with this text, and the mail goes to the receiving member's sign-up email as the admin edited it. {APPENDED}
        {' '}Without a version, material mail cannot be sent.
      </p>
      {has('terms.edit') && (
        <div className="mb-4"><Button onClick={() => setCreating(true)}>New version</Button></div>
      )}
      <QueryState isPending={list.isPending} error={list.error} empty={rows.length === 0}
        emptyText="There are no trade material mail templates.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Version</th><th className={TH}>Subject</th>
            <th className={TH}>Effective at</th><th className={TH}>Created by</th><th className={TH}>Created at</th>
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.version}>
                <td className={TD}>
                  <button type="button" className="text-teal-700 underline dark:text-teal-400"
                    onClick={() => setViewing(t.version)}>v{t.version}</button>
                  {current === t.version && <Badge tone="ok">In effect</Badge>}
                </td>
                <td className={TD}>{t.subject}</td>
                <td className={TD}><Time ms={t.effectiveAt} /></td>
                <td className={TD}><AdminRef adminId={t.createdByAdminId} empty="System" /></td>
                <td className={TD}><Time ms={t.createdAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      {viewing !== null && (
        <ViewTemplateDialog version={viewing} effective={current} onClose={() => setViewing(null)} />
      )}
      {creating && <NewTemplateDialog base={effective.data ?? null} onClose={() => setCreating(false)} />}
    </>
  )
}

/** EA-74 — 판 보기(XM-112). 제목·본문은 글로만(HTML 로 그리지 않는다). */
function ViewTemplateDialog({ version, effective, onClose }: {
  version: number
  effective: number | null
  onClose: () => void
}) {
  const q = useQuery({
    queryKey: ['trade-material-mail-templates', version], queryFn: () => getTradeMaterialMailTemplate(version),
  })
  const t = q.data
  return (
    <ViewDialog open title={`${MATERIAL_MAIL} v${version}`} onClose={onClose}>
      <QueryState isPending={q.isPending} error={q.error}>
        {t && (
          <>
            <dl className="mb-3 grid grid-cols-[6rem_1fr] gap-1.5">
              <Row label="Effective at">
                <Time ms={t.effectiveAt} />
                {effective === t.version && <Badge tone="ok">In effect</Badge>}
              </Row>
              <Row label="Created by"><AdminRef adminId={t.createdByAdminId} empty="System" /></Row>
              <Row label="Created at"><Time ms={t.createdAt} /></Row>
            </dl>
            <div className="rounded border border-slate-200 p-3 dark:border-slate-700">
              <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
                <Row label="Subject"><Plain text={t.subject} /></Row>
                <Row label="Body"><Plain text={t.body} /></Row>
              </dl>
            </div>
          </>
        )}
      </QueryState>
    </ViewDialog>
  )
}

/**
 * EA-75 — 새 판(XM-113 `{subject, body, effectiveAt?}`). 처음 값은 시행 중 판(없으면 빈 칸 — EA-67 과 같게). 두 칸 모두
 * 필수(서버 @NotBlank — 공백만이면 400), 한글이 있으면 잠김(서버도 400 `korean_not_allowed`).
 */
function NewTemplateDialog({ base, onClose }: { base: TradeMaterialMailTemplate | null; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const [subject, setSubject] = useState(base?.subject ?? '')
  const [body, setBody] = useState(base?.body ?? '')
  const [effectiveAt, setEffectiveAt] = useState('')

  const { ms: effectiveMs, bad: badDate } = effectiveMillis(effectiveAt)
  const blank = subject.trim() === '' || body.trim() === ''
  const korean = hasKorean(subject) || hasKorean(body)

  async function submit() {
    const send = async () => {
      const t = await createTradeMaterialMailTemplate({ subject, body }, effectiveMs)
      setFlash({ tone: 'ok', text: `Created ${MATERIAL_MAIL} v${t.version}.` })
      await qc.invalidateQueries({ queryKey: ['trade-material-mail-templates'] })
    }
    await submitWrite(w, `${MATERIAL_MAIL} — new version`, send, onClose)
  }

  return (
    <Confirm open wide title={`${MATERIAL_MAIL} — new version`} action="Create" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={blank || korean || badDate}>
      <div className="flex flex-col gap-3">
        <Field label="Mail subject (required · 200 chars)" value={subject} maxLength={200}
          onChange={(e) => setSubject(e.target.value)} />
        <TextArea label="Mail body (required · 200,000 chars)" value={body} maxLength={200000} rows={8}
          onChange={(e) => setBody(e.target.value)} />
        <EnglishOnlyNote texts={[subject, body]} hint={HINT} blocked={BLOCKED} />
        <DateTimeField label="Effective at (optional)" name="Effective at" value={effectiveAt} onChange={setEffectiveAt} />
        {badDate && <Notice tone="stop">The effective time cannot be read.</Notice>}
        <p className="text-xs text-slate-500">
          Saves as a new version. Old versions are not deleted. From the effective time, material mail uses this
          version. {APPENDED}
        </p>
      </div>
    </Confirm>
  )
}
